import * as RadioPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { WithChildren } from '../../../types/component.types';

const radioGroupRoot = cva('flex shrink-0 flex-wrap', {
  variants: {
    space: {
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      s: 'gap-s',
      m: 'gap-m',
      l: 'gap-l',
    },
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col',
    },
  },
  defaultVariants: {
    space: 's',
    orientation: 'vertical',
  },
});

type CombinedProps = WithChildren &
  VariantProps<typeof radioGroupRoot> &
  Omit<
    RadioPrimitive.RadioGroupProps,
    'className' | 'onChange' | 'onValueChange'
  >;

export type RadioGroupProps = CombinedProps & {
  onChange?: RadioPrimitive.RadioGroupProps['onValueChange'];
};

export const RadioGroup = ({
  defaultValue,
  children,
  onChange,
  value,
  space = 's',
  orientation,
  ...primitiveProps
}: RadioGroupProps) => {
  if (!primitiveProps['aria-label'] && !primitiveProps['aria-labelledby']) {
    throw new Error(
      'RadioGroup requires an aria-label or aria-labelledby prop.',
    );
  }

  return (
    <RadioPrimitive.Root
      {...primitiveProps}
      value={value}
      className={radioGroupRoot({ space, orientation })}
      defaultValue={defaultValue}
      onValueChange={(value) => onChange?.(value)}
    >
      {children}
    </RadioPrimitive.Root>
  );
};
